/**
 * Documentation Tab Component
 *
 * Displays official documentation links for the enterprise system
 * Organized by type: Developer, API, Admin, Release Notes, etc.
 */

import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ExternalLink,
  BookOpen,
  Code,
  Settings,
  FileText,
  Award,
  Link as LinkIcon,
  Shield,
  Loader2,
  AlertCircle,
  Globe,
  Lock,
  Building2,
} from 'lucide-react';
import { getDocumentationLinks, DocumentationLink } from '../../lib/api/enterpriseSystemsApi';

interface DocumentationTabProps {
  systemId: string;
}

const DocumentationTab: React.FC<DocumentationTabProps> = ({ systemId }) => {
  // Fetch documentation links
  const {
    data,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['documentation-links', systemId],
    queryFn: () => getDocumentationLinks(systemId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Group links by type
  const groupedLinks = useMemo(() => {
    if (!data) return {};

    const groups: Record<string, DocumentationLink[]> = {};

    data.forEach((link) => {
      const type = link.link_type || 'other';
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(link);
    });

    return groups;
  }, [data]);

  /**
   * Get icon for link type
   */
  const getLinkTypeIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'developer_guide':
        return BookOpen;
      case 'api_docs':
      case 'api_documentation':
      case 'api_reference':
        return Code;
      case 'admin_guide':
        return Settings;
      case 'release_notes':
        return FileText;
      case 'best_practices':
        return Award;
      case 'integration_guide':
        return LinkIcon;
      case 'security_guide':
        return Shield;
      case 'user_guide':
        return BookOpen;
      case 'support_portal':
        return Settings;
      case 'community_forum':
        return Globe;
      case 'training_materials':
        return BookOpen;
      case 'troubleshooting':
      case 'troubleshooting_guide':
        return Settings;
      default:
        return FileText;
    }
  };

  /**
   * Get access level badge color
   */
  const getAccessLevelColor = (level: string) => {
    switch (level) {
      case 'public':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'licensed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'internal':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  /**
   * Get access level icon
   */
  const getAccessLevelIcon = (level: string) => {
    switch (level) {
      case 'public':
        return Globe;
      case 'licensed':
        return Lock;
      case 'internal':
        return Building2;
      default:
        return Globe;
    }
  };

  /**
   * Format link type for display
   */
  const formatLinkType = (type: string): string => {
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return 'N/A';
    }
  };

  // Loading State
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-primary-600 animate-spin mx-auto mb-3" />
          <p className="text-sm text-neutral-600">Loading documentation links...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">
            Failed to Load Documentation
          </h3>
          <p className="text-sm text-neutral-600">
            {error instanceof Error ? error.message : 'An error occurred.'}
          </p>
        </div>
      </div>
    );
  }

  // Empty State
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center max-w-md">
          <BookOpen className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">
            No Documentation Links
          </h3>
          <p className="text-sm text-neutral-600 mb-4">
            No documentation links have been added for this system yet.
          </p>
          <p className="text-xs text-neutral-500">
            Trigger <strong>AI Research</strong> to automatically discover and add official
            documentation links, or add them manually.
          </p>
        </div>
      </div>
    );
  }

  // Define section order (matching backend DocumentationType enum)
  const sectionOrder = [
    'developer_guide',
    'api_docs',
    'api_documentation',
    'user_guide',
    'admin_guide',
    'release_notes',
    'best_practices',
    'support_portal',
    'community_forum',
    'training_materials',
    'troubleshooting',
    'troubleshooting_guide',
    'other',
  ];

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-neutral-900 mb-2">
          Documentation & Resources
        </h2>
        <p className="text-neutral-600">
          {data.length} official documentation link{data.length !== 1 ? 's' : ''}{' '}
          available
        </p>
      </div>

      {/* Documentation Sections */}
      <div className="space-y-8">
        {sectionOrder.map((sectionType) => {
          const links = groupedLinks[sectionType];
          if (!links || links.length === 0) return null;

          const Icon = getLinkTypeIcon(sectionType);

          return (
            <div key={sectionType}>
              {/* Section Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <Icon className="w-5 h-5 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-neutral-900">
                  {formatLinkType(sectionType)}
                </h3>
                <span className="text-sm text-neutral-500">({links.length})</span>
              </div>

              {/* Links Grid */}
              <div className="grid grid-cols-1 gap-4">
                {links.map((link) => {
                  const AccessIcon = getAccessLevelIcon(link.access_level);

                  return (
                    <a
                      key={link.link_id}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block p-5 bg-white border-2 border-neutral-200 rounded-lg hover:border-primary-300 hover:shadow-md transition-all group"
                    >
                      {/* Link Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0 pr-4">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="text-base font-semibold text-neutral-900 group-hover:text-primary-600 transition-colors">
                              {link.title}
                            </h4>
                            <ExternalLink className="w-4 h-4 text-neutral-400 group-hover:text-primary-600 transition-colors flex-shrink-0" />
                          </div>

                          {link.description && (
                            <p className="text-sm text-neutral-600 leading-relaxed line-clamp-2">
                              {link.description}
                            </p>
                          )}
                        </div>

                        {/* Access Level Badge */}
                        <span
                          className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border whitespace-nowrap ${getAccessLevelColor(
                            link.access_level
                          )}`}
                        >
                          <AccessIcon className="w-3.5 h-3.5" />
                          {link.access_level.charAt(0).toUpperCase() +
                            link.access_level.slice(1)}
                        </span>
                      </div>

                      {/* Link Metadata */}
                      <div className="flex items-center gap-4 text-xs text-neutral-500">
                        {link.version && (
                          <span className="flex items-center gap-1">
                            <span className="font-medium">Version:</span>
                            {link.version}
                          </span>
                        )}
                        {link.language && link.language !== 'en' && (
                          <span className="flex items-center gap-1">
                            <span className="font-medium">Language:</span>
                            {link.language.toUpperCase()}
                          </span>
                        )}
                        {link.last_verified && (
                          <span className="flex items-center gap-1">
                            <span className="font-medium">Verified:</span>
                            {formatDate(link.last_verified)}
                          </span>
                        )}
                      </div>

                      {/* URL Preview */}
                      <div className="mt-2 pt-2 border-t border-neutral-100">
                        <p className="text-xs text-neutral-400 truncate font-mono">
                          {link.url}
                        </p>
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DocumentationTab;
